import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  imagePreview?: string;
  actions?: { action: string; description?: string; url: string }[];
  toolCalls?: string[];
  timestamp: Date;
}

export default function AIAssistant() {
  const { t } = useTranslation('assistant');
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.auth?.user);
  const [open, setOpen] = useState(() => sessionStorage.getItem('flux-ai-open') === 'true');
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>(() => {
    try { const s = sessionStorage.getItem('flux-ai-chat'); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [imageMimeType, setImageMimeType] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const QUICK_ACTIONS = [
    { icon: '🖥️', label: t('analyzeHosts'), prompt: t('analyzeHostsPrompt') },
    { icon: '🔔', label: t('checkAlerts'), prompt: t('checkAlertsPrompt') },
    { icon: '⬆️', label: t('uptimeStatus'), prompt: t('uptimeStatusPrompt') },
    { icon: '🐛', label: t('errorAnalysis'), prompt: t('errorAnalysisPrompt') },
    { icon: '🔒', label: t('securityReview'), prompt: t('securityReviewPrompt') },
    { icon: '📊', label: t('apmOverview'), prompt: t('apmOverviewPrompt') },
  ];

  const ANALYTICS_ACTIONS = [
    { icon: '📈', label: t('productAnalytics'), prompt: t('productAnalyticsPrompt') },
    { icon: '🔄', label: t('conversionFunnels'), prompt: t('conversionFunnelsPrompt') },
    { icon: '👥', label: t('userCohorts'), prompt: t('userCohortsPrompt') },
    { icon: '🌐', label: t('seoAnalysis'), prompt: t('seoAnalysisPrompt') },
    { icon: '✅', label: t('complianceStatus'), prompt: t('complianceStatusPrompt') },
    { icon: '🧠', label: t('aiIncidents'), prompt: t('aiIncidentsPrompt') },
    { icon: '🗺️', label: t('serviceMap'), prompt: t('serviceMapPrompt') },
    { icon: '🗄️', label: t('databaseHealth'), prompt: t('databaseHealthPrompt') },
  ];

  const FAQ = [
    { label: t('faqSetup'), prompt: t('faqSetupPrompt') },
    { label: t('faqAlerts'), prompt: t('faqAlertsPrompt') },
    { label: t('faqSlos'), prompt: t('faqSlosPrompt') },
    { label: t('faqCloud'), prompt: t('faqCloudPrompt') },
    { label: t('faqRecordings'), prompt: t('faqRecordingsPrompt') },
    { label: t('faqDashboards'), prompt: t('faqDashboardsPrompt') },
  ];

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); try { sessionStorage.setItem('flux-ai-chat', JSON.stringify(messages)); } catch {} }, [messages]);
  useEffect(() => { if (open && !minimized) setTimeout(() => inputRef.current?.focus(), 100); }, [open, minimized]);

  const handleImage = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f || !f.type.startsWith('image/') || f.size > 5e6) return;
    const r = new FileReader();
    r.onload = () => { const s = r.result as string; setImagePreview(s); setImageBase64(s.split(',')[1]); setImageMimeType(f.type); };
    r.readAsDataURL(f);
    e.target.value = '';
  }, []);

  const clearImage = () => { setImagePreview(null); setImageBase64(null); setImageMimeType(null); };

  const send = async (text?: string) => {
    const content = text || input.trim();
    if (!content && !imageBase64) return;
    if (isStreaming) return;

    const userMsg: Message = { id: `u-${Date.now()}`, role: 'user', content: content || `📷 ${t('imageSent')}`, imagePreview: imagePreview || undefined, timestamp: new Date() };
    const newMsgs = [...messages, userMsg];
    setMessages(newMsgs);
    setInput('');
    clearImage();
    setIsStreaming(true);

    const aId = `a-${Date.now()}`;
    setMessages(prev => [...prev, { id: aId, role: 'assistant', content: '', actions: [], toolCalls: [], timestamp: new Date() }]);

    try {
      const apiUrl = (import.meta as any).env?.VITE_API_URL || '/api';
      const token = localStorage.getItem('token');
      // Enqueue job — fast POST, returns jobId in <100ms (CF-safe).
      const enq = await fetch(`${apiUrl}/v1/mcp/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ messages: newMsgs.map(m => ({ role: m.role, content: m.content })), imageBase64: imageBase64 || undefined, imageMimeType: imageMimeType || undefined }),
      });
      if (!enq.ok) throw new Error(`${enq.status}`);
      const enqData = await enq.json();
      const jobId = enqData?.data?.jobId;
      if (!jobId) throw new Error('No jobId');

      // Poll for new events every 1.5s. Each GET is fast → CF doesn't drop.
      let full = '';
      const acts: any[] = [];
      const tools: string[] = [];
      let cursor = 0;
      const POLL_MS = 1500;
      const MAX_POLLS = 800; // 20min ceiling

      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise(r => setTimeout(r, POLL_MS));
        let pollRes: Response;
        try {
          pollRes = await fetch(`${apiUrl}/v1/mcp/jobs/${jobId}?cursor=${cursor}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` },
          });
        } catch { continue; }
        if (!pollRes.ok) {
          if (pollRes.status === 404) throw new Error('Job expired');
          continue;
        }
        const pd = await pollRes.json();
        const events = pd?.data?.events || [];
        cursor = pd?.data?.cursor ?? cursor;
        for (const d of events) {
          if (d.type === 'text') { full += d.text; setMessages(p => p.map(m => m.id === aId ? { ...m, content: full } : m)); }
          else if (d.type === 'action') { acts.push(d.action); setMessages(p => p.map(m => m.id === aId ? { ...m, actions: [...acts] } : m)); }
          else if (d.type === 'tool_call') { tools.push(d.tool); setMessages(p => p.map(m => m.id === aId ? { ...m, toolCalls: [...tools] } : m)); }
          else if (d.type === 'error') { full = full ? `${full}\n\n⚠️ ${t('error')}` : t('error'); setMessages(p => p.map(m => m.id === aId ? { ...m, content: full } : m)); }
        }
        if (pd?.data?.done) break;
      }
    } catch {
      setMessages(p => p.map(m => m.id === aId ? { ...m, content: t('error') } : m));
    } finally {
      setIsStreaming(false);
    }
  };

  if (!user) return null;

  return (
    <>
      {!open && (
        <button onClick={() => { setOpen(true); sessionStorage.setItem('flux-ai-open', 'true'); }}
          className="fixed bottom-5 right-5 z-[100] h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 text-white shadow-lg shadow-blue-500/25 hover:shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center group">
          <svg className="h-6 w-6 group-hover:rotate-12 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" /></svg>
          <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-green-400 border-2 border-white dark:border-gray-900 text-[7px] font-bold flex items-center justify-center">AI</span>
        </button>
      )}

      {open && (
        <div className={`fixed z-[100] transition-all duration-300 ${minimized ? 'sm:h-auto sm:w-80' : ''} inset-0 sm:inset-auto sm:bottom-5 sm:right-5 sm:w-[440px] sm:h-[640px] sm:max-h-[85vh]`}>
          <div className={`flex flex-col h-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-2xl overflow-hidden sm:rounded-2xl ${minimized ? 'sm:rounded-xl' : ''}`}>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-gray-900 to-gray-800 text-white shrink-0">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082" /></svg>
                </div>
                <div>
                  <p className="font-bold text-sm">{t('title')}</p>
                  {!minimized && <p className="text-[10px] text-white/40">{t('poweredBy')}</p>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {!minimized && (
                  <button onClick={() => { setMessages([]); sessionStorage.removeItem('flux-ai-chat'); }} className={`p-1.5 rounded-lg hover:bg-white/10 transition-colors ${messages.length === 0 ? 'opacity-30' : ''}`} title={t('clearChat')} disabled={messages.length === 0}>🗑️</button>
                )}
                <button onClick={() => setMinimized(!minimized)} className="hidden sm:block p-1.5 rounded-lg hover:bg-white/10 transition-colors">⊡</button>
                <button onClick={() => { setOpen(false); setMinimized(false); sessionStorage.setItem('flux-ai-open', 'false'); }} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">✕</button>
              </div>
            </div>

            {!minimized && (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
                  {messages.length === 0 ? (
                    <div className="space-y-5">
                      <div className="text-center py-3">
                        <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center mx-auto mb-3 shadow-lg">
                          <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5" /></svg>
                        </div>
                        <h3 className="font-bold text-base dark:text-white">{t('welcome')} {user?.name?.split(' ')[0] || user?.firstName || 'there'}!</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('welcomeDesc')}</p>
                      </div>

                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">{t('quickActions')}</p>
                        <div className="grid grid-cols-2 gap-2">
                          {QUICK_ACTIONS.map((a, i) => (
                            <button key={i} onClick={() => send(a.prompt)}
                              className="flex flex-col items-center gap-2 p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-all text-center group">
                              <span className="text-xl group-hover:scale-110 transition-transform">{a.icon}</span>
                              <span className="text-[11px] font-medium dark:text-gray-300 leading-tight">{a.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">{t('analyticsMore')}</p>
                        <div className="grid grid-cols-2 gap-2">
                          {ANALYTICS_ACTIONS.map((a, i) => (
                            <button key={i} onClick={() => send(a.prompt)}
                              className="flex flex-col items-center gap-2 p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700 hover:bg-purple-50 dark:hover:bg-purple-950/30 transition-all text-center group">
                              <span className="text-xl group-hover:scale-110 transition-transform">{a.icon}</span>
                              <span className="text-[11px] font-medium dark:text-gray-300 leading-tight">{a.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">{t('commonQuestions')}</p>
                        <div className="space-y-1.5">
                          {FAQ.map((f, i) => (
                            <button key={i} onClick={() => send(f.prompt)}
                              className="w-full flex items-center gap-3 p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-all text-left">
                              <span className="text-gray-400">💬</span>
                              <span className="text-xs font-medium dark:text-gray-300">{f.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                        <div className={`h-7 w-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 text-sm ${msg.role === 'user' ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-gray-800'}`}>
                          {msg.role === 'user' ? '👤' : '🤖'}
                        </div>
                        <div className={`max-w-[85%] space-y-2 ${msg.role === 'user' ? 'flex flex-col items-end' : ''}`}>
                          {msg.imagePreview && <img src={msg.imagePreview} alt="" className="max-w-[200px] rounded-xl" />}
                          {msg.toolCalls?.length && !msg.content ? (
                            <div className="flex items-center gap-2 text-xs text-gray-400 px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                              <div className="h-3 w-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                              {t('analyzing')} {msg.toolCalls[msg.toolCalls.length - 1].replace(/_/g, ' ')}...
                            </div>
                          ) : null}
                          {msg.content && (
                            <div className={`rounded-2xl px-4 py-3 text-sm ${msg.role === 'user' ? 'bg-blue-600 text-white rounded-tr-md' : 'bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-tl-md dark:text-gray-200'}`}>
                              {msg.role === 'assistant' ? (
                                <div className="prose prose-sm dark:prose-invert max-w-none [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0.5 [&_h1]:text-base [&_h2]:text-sm [&_h2]:font-bold [&_h3]:text-sm [&_code]:text-xs [&_code]:bg-black/10 [&_code]:dark:bg-white/10 [&_code]:px-1 [&_code]:rounded [&_pre]:bg-gray-900 [&_pre]:text-gray-100 [&_pre]:rounded-xl [&_pre]:p-3 [&_pre]:text-xs [&_pre]:overflow-x-auto [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_strong]:font-bold">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                                </div>
                              ) : <span className="whitespace-pre-wrap">{msg.content}</span>}
                            </div>
                          )}
                          {!msg.content && !msg.toolCalls?.length && (
                            <div className="flex gap-1 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 rounded-2xl rounded-tl-md border border-gray-200 dark:border-gray-700">
                              <span className="h-2 w-2 rounded-full bg-blue-400 animate-bounce" />
                              <span className="h-2 w-2 rounded-full bg-blue-400 animate-bounce [animation-delay:0.15s]" />
                              <span className="h-2 w-2 rounded-full bg-blue-400 animate-bounce [animation-delay:0.3s]" />
                            </div>
                          )}
                          {msg.actions?.map((a, i) => (
                            <button key={i} onClick={() => { navigate(a.url); setOpen(false); sessionStorage.setItem('flux-ai-open', 'false'); }}
                              className="w-full flex items-center gap-3 p-3 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all text-left">
                              <div className="h-10 w-10 rounded-xl bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center shrink-0 text-lg">→</div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold dark:text-white">{a.action}</p>
                                {a.description && <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{a.description}</p>}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                  {messages.length > 0 && !isStreaming && (
                    <div className="flex flex-wrap gap-1.5 pt-2">
                      {[...QUICK_ACTIONS.slice(0, 3), ...ANALYTICS_ACTIONS.slice(0, 3)].map(a => (
                        <button key={a.label} onClick={() => send(a.prompt)}
                          className="px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 text-[11px] font-medium text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-all">
                          {a.icon} {a.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <div ref={endRef} />
                </div>

                {imagePreview && (
                  <div className="px-4 pb-2">
                    <div className="relative inline-block">
                      <img src={imagePreview} alt="" className="h-16 rounded-lg border" />
                      <button onClick={clearImage} className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center text-xs">✕</button>
                    </div>
                  </div>
                )}

                <div className="p-3 border-t border-gray-200 dark:border-gray-800 shrink-0">
                  <div className="flex items-end gap-2">
                    <button onClick={() => fileRef.current?.click()} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0" title={t('uploadImage')}>
                      📷
                    </button>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImage} />
                    <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                      placeholder={t('placeholder')}
                      className="flex-1 resize-none rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400 dark:focus:border-blue-600 max-h-[100px] min-h-[40px] dark:text-white"
                      rows={1} disabled={isStreaming} />
                    <button onClick={() => send()} disabled={isStreaming || (!input.trim() && !imageBase64)}
                      className={`p-2.5 rounded-xl transition-all shrink-0 ${(!input.trim() && !imageBase64) || isStreaming ? 'bg-gray-100 dark:bg-gray-800 text-gray-400' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'}`}>
                      {isStreaming ? <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : '➤'}
                    </button>
                  </div>
                  <p className="text-[9px] text-gray-400 text-center mt-1.5">{t('disclaimer')}</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
